import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { kpiData, areasOfImprovement, fiveYearRoadmap } from '../data/mockData';
import { ArrowRight, DollarSign, TrendingUp, Calculator, Target, Zap, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const ImpactIntelligencePage = () => {
  const totalImpact = kpiData.reduce((sum, kpi) => sum + kpi.impactPer1Percent, 0);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl" />
            <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Impact Intelligence:
                <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Financial Impact Analysis & ROI Projections
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Quantify the financial impact of improving each KPI by 1%. Get executive-ready reports with ROI projections.
              </p>
            </div>
          </div>
        </section>

        {/* Impact Summary */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Every 1% Improvement Has a Dollar Value
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Impact Intelligence translates performance improvements into financial outcomes. Our AI analyzes your organization's data and calculates the monetary impact of improving each KPI by just 1%—giving CXOs clear justification for strategic investments.
                </p>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Whether you're prioritizing initiatives, building a business case, or reporting to the board, Impact Intelligence provides the numbers that matter.
                </p>

                <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-emerald-600 font-medium">Total Potential Impact (1% improvement each)</div>
                      <div className="text-3xl font-bold text-gray-900">${(totalImpact / 1000000).toFixed(1)}M</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Impact per KPI (1% Improvement)</h3>
                <div className="space-y-4">
                  {kpiData.slice(0, 6).map((kpi, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                      <span className="text-gray-700 font-medium">{kpi.name}</span>
                      <span className="text-emerald-600 font-bold">${(kpi.impactPer1Percent / 1000000).toFixed(1)}M</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Areas of Improvement */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Priority Areas for Improvement</h2>
              <p className="text-gray-600">AI-identified focus areas ranked by potential impact</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Area</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Priority</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Potential Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {areasOfImprovement.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="font-medium text-gray-900">{item.area}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.priority === 'High' ? 'bg-red-100 text-red-600' :
                          item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {item.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-emerald-600">
                        {item.potentialImpact}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* 5-Year Roadmap */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">5-Year Strategic Roadmap</h2>
              <p className="text-gray-600">AI-generated pathway to becoming an industry leader</p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-emerald-200 hidden md:block" />

              <div className="space-y-8">
                {fiveYearRoadmap.map((year, index) => (
                  <div key={index} className="relative flex gap-8">
                    {/* Timeline dot */}
                    <div className="hidden md:flex flex-shrink-0 w-16 h-16 bg-emerald-600 rounded-full items-center justify-center z-10">
                      <span className="text-white font-bold text-sm">Y{index + 1}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="md:hidden px-3 py-1 bg-emerald-600 text-white text-sm font-medium rounded-full">
                          {year.year}
                        </span>
                        <h3 className="text-xl font-bold text-gray-900">{year.focus}</h3>
                      </div>
                      <ul className="space-y-2">
                        {year.goals.map((goal, gIndex) => (
                          <li key={gIndex} className="flex items-center gap-2 text-gray-600">
                            <ChevronRight className="w-4 h-4 text-emerald-600" />
                            {goal}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Quantify Your Improvement Potential?
            </h2>
            <p className="text-emerald-100 text-lg mb-8">
              Get a personalized impact analysis for your organization with our free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 w-full sm:w-auto">
                  Start Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-700 w-full sm:w-auto">
                  Talk to an Expert
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ImpactIntelligencePage;
